// React-bound surface for the workspace tenancy contract. The URL
// (`:workspaceId` param) is the source of truth for "which workspace am I
// in"; this context reads the current param, cross-references it against
// the workspaces list, and owns the tenant-switch side effects (cache
// eviction, persistence, navigation). It also carries the platform identity
// (organizations, selector groups, platform-staff flag) that the workspace
// picker needs.

import { useQueryClient } from "@tanstack/react-query";
import { useParams, useRouter } from "@tanstack/react-router";
import { createContext, type ReactNode, useCallback, useContext } from "react";
import { tenantPredicate } from "@/api/keys";
import { useIdentity } from "@/api/me";
import type { OrganizationSummary, Workspace } from "@/api/types";
import { useWorkspaces } from "@/api/workspaces";
import { deriveSelectorGroups, type SelectorGroup } from "./workspace-derivation";
import { persistId } from "./workspace-storage";

export interface WorkspaceContextValue {
    /** Current workspace id, taken from the URL. Empty string while loading. */
    workspaceId: string;
    /** Current workspace display name. Empty string while loading. */
    workspaceName: string;
    /** Every workspace the user can access. */
    workspaces: Workspace[];
    /** Whether the workspaces list is still being fetched. */
    isLoading: boolean;
    /** Switch to a different workspace: evicts tenant-scoped queries, persists
     *  the id for the `/` and `:workspaceId`-guard fallback, and navigates to
     *  the same section under the new workspace. No-op for an id not in
     *  `workspaces`. */
    setWorkspace: (id: string) => void;
    /** Every organization the identity carries. */
    organizations: OrganizationSummary[];
    /** Organizations grouped with their workspaces, for the workspace picker. */
    selectorGroups: SelectorGroup[];
    /** Whether the caller is platform staff. */
    platformStaff: boolean;
}

/**
 * Workspace context. Production code should use `useWorkspaceContext()` — it
 * throws when the Provider is missing, which is a better DX than a silent
 * `null`. The raw Context is exported so test harnesses can inject a seeded
 * value directly instead of mocking the hook.
 */
export const WorkspaceContext = createContext<WorkspaceContextValue | null>(null);

// Switching workspaces from a deep route can't keep the entity id (it
// belongs to a different tenant) but should preserve the section the user
// was in. Pathname shape: /w/<wsId>/<section>/...; section is parts[2] (or
// undefined when somehow at the workspace root). Works for any future
// section without per-feature config.
export function deriveSwitchTarget(pathname: string, newId: string): string {
    const parts = pathname.split("/").filter(Boolean);
    const section = parts[2];
    return section ? `/w/${newId}/${section}` : `/w/${newId}`;
}

interface WorkspaceProviderProps {
    children: ReactNode;
}

export function WorkspaceProvider({ children }: WorkspaceProviderProps) {
    const queryClient = useQueryClient();
    const router = useRouter();
    // strict: false — this may render outside the /w/$workspaceId route
    // during navigation transitions; coalesce a missing param to "".
    const params = useParams({ strict: false }) as { workspaceId?: string };
    const currentId = params.workspaceId ?? "";

    const workspacesQuery = useWorkspaces();
    const identityQuery = useIdentity();
    const workspaces = workspacesQuery.data ?? [];
    const selected = workspaces.find((w) => w.id === currentId) ?? null;

    const setWorkspace = useCallback(
        (id: string) => {
            const match = workspaces.find((w) => w.id === id);
            if (!match) return;
            // Tenant boundary — evict tenant-scoped queries before navigating
            // so the new URL doesn't briefly paint the previous tenant's
            // data. The workspaces list itself is user-scoped (doesn't
            // change on switch) and is preserved by tenantPredicate.
            void queryClient.cancelQueries({ predicate: tenantPredicate });
            queryClient.removeQueries({ predicate: tenantPredicate });
            persistId(id);
            const target = deriveSwitchTarget(router.state.location.pathname, id);
            // history.push bypasses the typed-router overload — `target` is
            // a runtime string built from the current section name, which
            // the typed `to:` union can't accept without enumerating every
            // section.
            void router.history.push(target);
        },
        [workspaces, queryClient, router],
    );

    return (
        <WorkspaceContext.Provider
            value={{
                workspaceId: selected?.id ?? "",
                workspaceName: selected?.name ?? "",
                workspaces,
                isLoading: workspacesQuery.isLoading,
                setWorkspace,
                organizations: identityQuery.data?.organizations ?? [],
                selectorGroups: deriveSelectorGroups(identityQuery.data),
                platformStaff: identityQuery.data?.platformStaff ?? false,
            }}
        >
            {children}
        </WorkspaceContext.Provider>
    );
}

export function useWorkspaceContext(): WorkspaceContextValue {
    const ctx = useContext(WorkspaceContext);
    if (!ctx) {
        throw new Error("useWorkspaceContext must be used within a WorkspaceProvider");
    }
    return ctx;
}

export { readStoredId } from "./workspace-storage";
